import React from "react";
import { MobileScreenSettingsWithRelations } from "@/types/mobile-settings";
import { PresignedImage } from "@/components/common/PresignedImage";
import {
  MapPin,
  Calendar,
  Users,
  ChevronRight,
  Menu,
  Bed,
  Home,
  Car,
  Plane,
  Image as ImageIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface GuestAppPreviewProps {
  settings: MobileScreenSettingsWithRelations;
}

// Static mockup preview — hardcoded strings are intentional placeholder content,
// not user-facing text that needs i18n.
export const GuestAppPreview: React.FC<GuestAppPreviewProps> = ({
  settings,
}) => {
  const previewRef = React.useRef<HTMLDivElement>(null);
  const appTextColor = settings.appTextColor || "#111827";
  const appBgColor = settings.appBgColor || "#FFFFFF";
  const isDarkPreview = appBgColor.toLowerCase() === "#111827";
  const mutedTextClass = isDarkPreview ? "text-slate-300" : "text-zinc-500";
  const surfaceClass = isDarkPreview
    ? "bg-slate-900 border-slate-700 shadow-black/40"
    : "bg-white border-zinc-100 shadow-lg";
  const subtleSurfaceClass = isDarkPreview
    ? "bg-slate-800 border-slate-700"
    : "bg-zinc-50 border-zinc-900";
  const primaryButtonClass = isDarkPreview
    ? "bg-slate-50 text-slate-950"
    : "bg-zinc-900 text-white";
  const outlineButtonClass = isDarkPreview
    ? "bg-transparent text-slate-50 border-slate-50"
    : "bg-white text-zinc-900 border-zinc-900";

  React.useEffect(() => {
    const preview = previewRef.current;

    if (!preview) return;

    preview.style.setProperty("--guest-app-text", appTextColor);
    preview.style.setProperty("--guest-app-bg", appBgColor);
  }, [appBgColor, appTextColor]);

  return (
    <div
      ref={previewRef}
      className="font-patrick hide-scrollbar flex h-full flex-col overflow-y-auto bg-[var(--guest-app-bg)] text-[var(--guest-app-text)]"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <button
          className={`flex size-10 items-center justify-center rounded-full border ${subtleSurfaceClass}`}
        >
          <Menu className="size-5" />
        </button>
      </div>

      {/* Hero / Carousel Placeholder */}
      <div className="px-4">
        <div
          className={`relative aspect-[16/9] overflow-hidden rounded-2xl ${subtleSurfaceClass}`}
        >
          {settings.hotel?.carouselPhotos?.[0] ? (
            <PresignedImage
              fileId={settings.hotel.carouselPhotos[0].photoId}
              className="size-full object-cover"
            />
          ) : (
            <div
              className={`flex size-full items-center justify-center ${mutedTextClass}`}
            >
              <ImageIcon className="size-12" />
            </div>
          )}
          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div
                key={i}
                className={`h-1 rounded-full ${i === 1 ? "w-10 bg-current" : "w-6 bg-current opacity-25"}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Welcome Card */}
      <div className="p-4">
        <div className={`space-y-4 rounded-2xl p-4 ${surfaceClass}`}>
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h2 className="text-xl font-bold">Mira Malkovich</h2>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-[10px] font-medium">
                  <Bed className="size-3.5" />
                  <span>Luxury Sea View 404 | 404</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] font-medium">
                  <Home className="size-3.5" />
                  <span>{settings.hotel?.name || "HOTEL BRAND #1"}</span>
                </div>
                <div
                  className={`flex items-start gap-2 text-[10px] ${mutedTextClass}`}
                >
                  <MapPin className="size-3.5 shrink-0" />
                  <span className="leading-tight">
                    {settings.hotel?.hotelAddress?.fullAddress ||
                      "Volodymyra Velykoho 10b street,\nLviv, Ukraine 96002"}
                  </span>
                </div>
                <div
                  className={`flex items-center gap-2 text-[10px] ${mutedTextClass}`}
                >
                  <Users className="size-3.5" />
                  <span>2 adults, 1 child</span>
                </div>
                <div
                  className={`flex items-center gap-2 text-[10px] ${mutedTextClass}`}
                >
                  <Calendar className="size-3.5" />
                  <span>16.06.2025 | 3:00 PM</span>
                </div>
              </div>
            </div>
            <div
              className={`flex size-20 shrink-0 flex-col items-center justify-center gap-1 rounded-xl border ${subtleSurfaceClass}`}
            >
              <ImageIcon className={`size-6 ${mutedTextClass}`} />
              <span className="text-[10px] font-bold">Logo</span>
            </div>
          </div>

          <div className="space-y-2">
            <button
              className={`flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold ${primaryButtonClass}`}
            >
              Check-In | 15:00
            </button>
            <button
              className={`w-full rounded-lg border py-2.5 text-xs font-bold ${outlineButtonClass}`}
            >
              Upload your documents
            </button>
          </div>
        </div>
      </div>

      {/* Shortcuts */}
      <div className="hide-scrollbar flex gap-2 overflow-x-auto px-4">
        {settings.mobileScreenSettingsShortcuts.map((shortcut) => (
          <div
            key={shortcut.id}
            className={`flex shrink-0 items-center gap-2 rounded-sm border px-3 py-2 ${subtleSurfaceClass}`}
          >
            <div
              className={`flex size-4 items-center justify-center rounded-sm ${subtleSurfaceClass}`}
            >
              <ImageIcon className={`size-3 ${mutedTextClass}`} />
            </div>
            <span className="text-[10px] font-bold">{shortcut.name}</span>
          </div>
        ))}
      </div>

      {/* Services List */}
      <div className="space-y-0.5 px-4 py-2">
        <div className="flex items-center justify-between border-b border-current/10 py-3">
          <div className="flex items-center gap-3">
            <Car className="size-5" />
            <span className="text-xs font-bold">Add pickup to hotel</span>
          </div>
          <ChevronRight className={`size-4 ${mutedTextClass}`} />
        </div>
        <div className="flex items-center justify-between border-b border-current/10 py-3">
          <div className="flex items-center gap-3">
            <Plane className="size-5" />
            <span className="text-xs font-bold">Transfer to Airport</span>
          </div>
          <ChevronRight className={`size-4 ${mutedTextClass}`} />
        </div>
      </div>

      {/* Recommended Services */}
      <div className="mt-4 space-y-3 px-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold underline decoration-zinc-300 underline-offset-4">
            Recommended{" "}
            <span className="underline decoration-zinc-900">Services</span>
          </h3>
          <button
            className={`rounded-lg px-3 py-1.5 text-[10px] font-bold ${primaryButtonClass}`}
          >
            View All
          </button>
        </div>
        <div className="hide-scrollbar flex gap-3 overflow-x-auto pb-2">
          {settings.mobileScreenSettingsRecommendedServices.map((rs) => (
            <div
              key={rs.id}
              className={`w-36 shrink-0 space-y-4 rounded-xl border p-3 ${subtleSurfaceClass}`}
            >
              <Badge
                className={`h-4 rounded-full px-2 py-0 text-[8px] ${primaryButtonClass}`}
              >
                {rs.service.category.name}
              </Badge>
              <div className="space-y-1">
                <div className="text-[11px] leading-tight font-bold">
                  {rs.service.name}
                </div>
                <div className={`text-right text-[9px] ${mutedTextClass}`}>
                  {rs.service.price}€/pers.
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Featured Activities */}
      <div className="mt-4 mb-8 space-y-3 px-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold underline decoration-zinc-300 underline-offset-4">
            Featured{" "}
            <span className="underline decoration-zinc-900">Activities</span>
          </h3>
          <button
            className={`rounded-lg px-3 py-1.5 text-[10px] font-bold ${primaryButtonClass}`}
          >
            View All
          </button>
        </div>
        <div className="hide-scrollbar flex gap-3 overflow-x-auto pb-2">
          {settings.mobileScreenSettingsFeaturedActivities.map((fa) => (
            <div
              key={fa.id}
              className={`w-36 shrink-0 space-y-4 rounded-xl border p-3 ${subtleSurfaceClass}`}
            >
              <Badge
                className={`h-4 rounded-full px-2 py-0 text-[8px] ${primaryButtonClass}`}
              >
                {fa.service.category.name}
              </Badge>
              <div className="space-y-1">
                <div className="text-[11px] leading-tight font-bold">
                  {fa.service.name}
                </div>
                <div className={`text-right text-[9px] ${mutedTextClass}`}>
                  {fa.service.price}€/pers.
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {settings.mobileScreenSettingsCtaServices.length > 0 ? (
        <div className="mt-4 space-y-3 px-4">
          <h3 className="text-sm font-bold underline decoration-zinc-300 underline-offset-4">
            Call to{" "}
            <span className="underline decoration-zinc-900">Action</span>
          </h3>
          <div className="hide-scrollbar flex gap-3 overflow-x-auto pb-2">
            {settings.mobileScreenSettingsCtaServices.map((cs) => (
              <div
                key={cs.id}
                className={`w-36 shrink-0 space-y-4 rounded-xl border p-3 ${subtleSurfaceClass}`}
              >
                <Badge
                  className={`h-4 rounded-full px-2 py-0 text-[8px] ${primaryButtonClass}`}
                >
                  {cs.service.category.name}
                </Badge>
                <div className="space-y-1">
                  <div className="text-[11px] leading-tight font-bold">
                    {cs.service.name}
                  </div>
                  <div className={`text-right text-[9px] ${mutedTextClass}`}>
                    {cs.service.price}€/pers.
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {settings.mobileScreenSettingsPages.length > 0 ? (
        <div className="mt-4 space-y-0.5 px-4 py-2">
          <h3 className="mb-1 text-sm font-bold underline decoration-zinc-300 underline-offset-4">
            Pages
          </h3>
          {settings.mobileScreenSettingsPages.map((pageRelation) => (
            <div
              key={pageRelation.id}
              className="flex items-center justify-between border-b border-current/10 py-3"
            >
              <span className="text-xs font-bold">
                {pageRelation.customPage.buttonTitle ||
                  pageRelation.customPage.title}
              </span>
              <ChevronRight className={`size-4 ${mutedTextClass}`} />
            </div>
          ))}
        </div>
      ) : null}

      {(settings.mobileScreenSettingsFaqs ?? []).length > 0 ? (
        <div className="mt-4 space-y-3 px-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold underline decoration-zinc-300 underline-offset-4">
              Recommended{" "}
              <span className="underline decoration-zinc-900">FAQs</span>
            </h3>
            <button
              className={`rounded-lg px-3 py-1.5 text-[10px] font-bold ${primaryButtonClass}`}
            >
              View All
            </button>
          </div>
          <div className="space-y-2">
            {(settings.mobileScreenSettingsFaqs ?? []).map((faqRelation) => (
              <div
                key={faqRelation.id}
                className={`space-y-2 rounded-xl border p-3 ${surfaceClass}`}
              >
                {faqRelation.faq.category?.name ? (
                  <Badge
                    className={`h-4 rounded-full px-2 py-0 text-[8px] ${primaryButtonClass}`}
                  >
                    {faqRelation.faq.category.name}
                  </Badge>
                ) : null}
                <div className="text-[11px] leading-tight font-bold">
                  {faqRelation.faq.question}
                </div>
                <div
                  className={`line-clamp-3 text-[9px] leading-relaxed ${mutedTextClass}`}
                >
                  {faqRelation.faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Navigation Bar */}
      <div
        className={`sticky bottom-0 mt-auto flex justify-around border-t border-current/10 p-2 ${isDarkPreview ? "bg-slate-900" : "bg-white"}`}
      >
        <div className="flex flex-col items-center gap-1">
          <div className="size-4 rounded-sm bg-current" />
          <span className="text-[8px]">Room</span>
        </div>
        <div className={`flex flex-col items-center gap-1 ${mutedTextClass}`}>
          <div className="size-4 rounded-sm bg-current opacity-25" />
          <span className="text-[8px]">Activities</span>
        </div>
        <div className={`flex flex-col items-center gap-1 ${mutedTextClass}`}>
          <div className="size-4 rounded-sm bg-current opacity-25" />
          <span className="text-[8px]">Concierge</span>
        </div>
        <div className={`flex flex-col items-center gap-1 ${mutedTextClass}`}>
          <div className="size-4 rounded-sm bg-current opacity-25" />
          <span className="text-[8px]">Requests</span>
        </div>
        <div className={`flex flex-col items-center gap-1 ${mutedTextClass}`}>
          <div className="size-4 rounded-sm bg-current opacity-25" />
          <span className="text-[8px]">Account</span>
        </div>
      </div>
    </div>
  );
};
